import axios, {
  AxiosInstance,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from "axios";

/**
 * Host-provided Axios interceptors for {@link BaseClient}.
 * Wire these from the app that constructs the client (demo host, partner page, etc.).
 */
export interface IHttpClientHandlers {
  onRequest?: (
    config: InternalAxiosRequestConfig,
  ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;
  onRequestError?: (error: unknown) => unknown;
  onResponse?: (
    response: AxiosResponse,
  ) => AxiosResponse | Promise<AxiosResponse>;
  onResponseError?: (error: unknown) => unknown;
}

/**
 * Axios create options plus host interceptor hooks.
 * `handlers` are applied after `axios.create`, not as Axios defaults.
 */
export interface IApiClientConfig extends CreateAxiosDefaults {
  handlers?: IHttpClientHandlers;
}

/**
 * The base client that should be extended.
 *
 * Sets up the Axios instance and optional host-provided request/response handlers.
 */
export abstract class BaseClient {
  protected _instance: AxiosInstance;

  constructor(config: IApiClientConfig = {}) {
    const { handlers, ...axiosConfig } = config;
    const instance = axios.create(axiosConfig);

    if (handlers?.onRequest || handlers?.onRequestError) {
      instance.interceptors.request.use(
        handlers.onRequest ?? ((requestConfig) => requestConfig),
        handlers.onRequestError ?? ((error) => Promise.reject(error)),
      );
    }

    if (handlers?.onResponse || handlers?.onResponseError) {
      instance.interceptors.response.use(
        handlers.onResponse ?? ((response) => response),
        handlers.onResponseError ?? ((error) => Promise.reject(error)),
      );
    }

    this._instance = instance;
  }
}
